import React, { useEffect, useState } from "react";
import CustomButton from "../../components/form/CustomButton";
import BaseModalAnimated from "../../components/modal/BaseModalAnimated";
import { getNewPlan, paymentsMethodsResource, paymentsResource } from "../../helpers/configRoutes/backend_helper";
import { showFormErrors } from "../../helpers/showFormErrors";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useFormik } from "formik";
import * as Yup from "yup";
import CustomInput from "../../components/form/CustomInput";
import CustomSelect from "../../components/form/CustomSelect";

import { useSelector } from "react-redux";

export default function ConfirmPayment({ modal, setModal }) {

    const [loading, setLoading] = useState(false);
    const [addMethod, setAddMethod] = useState(false)
    const [selectedMethod, setSelectedMethod] = useState({});
    const [paymentMethods, setPaymentMethods] = useState(false)
    const [changeMethod, setChangeMethod] = useState(false)

    useEffect(() => {
        const getMethods = async () => {
            const response = await paymentsMethodsResource("get", { params: { selext: true } });
            if (response.status) {
                let method = response.methods.length ? response.methods.find(m => {
                    if (m.principal)
                        return m
                }) : {}
                setPaymentMethods(response.methods)
                setSelectedMethod(method)
            }
        };
        getMethods();
    }, [addMethod]);

    const plan = modal?.plan;

    const price = Number(plan.price);
    const taxes = Number(plan.taxes || 0);
    const taxAmount = (price * taxes) / 100;
    const total = price + taxAmount;


    const save = async () => {
        if (!plan?.id || !selectedMethod) return;

        setLoading(true);
        try {
            const response = await getNewPlan({
                plan_id: plan.id,
                method_id: selectedMethod.id,
                recurrent_payment: false
            });

            if (response.status) {
                setModal({ show: false, plan: null });
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const stripe = useStripe();
    const elements = useElements();

    const baseItem = {
        id: null,
        card_name: "",
        alias: "",
        principal: false,
    };

    const validation = useFormik({
        initialValues: baseItem,
        enableReinitialize: true,
        validationSchema: Yup.object({
            card_name: Yup.string().required(
                "Card holder name is required."
            ),
            alias: Yup.string().nullable(),
            principal: Yup.boolean(),
        }),
        onSubmit: async (values, { setErrors }) => {
            setLoading(true);

            try {
                if (!stripe || !elements) {
                    setErrors({
                        card_name: "Stripe is not available at the moment.",
                    });
                    setLoading(false);
                    return;
                }

                const cardElement = elements.getElement(CardElement);
                if (!cardElement) {
                    setErrors({
                        card_name: "Unable to read card information.",
                    });
                    setLoading(false);
                    return;
                }

                const { error, paymentMethod } =
                    await stripe.createPaymentMethod({
                        type: "card",
                        card: cardElement,
                        billing_details: { name: values.card_name },
                    });

                if (error) {
                    throw new Error(error.message);
                }

                const dataToSend = {
                    ...values,
                    paymentMethodId: paymentMethod.id,
                    last4: paymentMethod.card.last4,
                    exp_month: paymentMethod.card.exp_month,
                    exp_year: paymentMethod.card.exp_year
                };

                const response = await paymentsMethodsResource(
                    values.id ? "put" : "post",
                    dataToSend,
                    values.id
                );

                if (!response.status) {
                    throw new Error(
                        response.errors || "Error saving payment method."
                    );
                }

                setSelectedMethod(response.method);
                setAddMethod(false)
            } catch (err) {
                setErrors({ card_name: err.message });
            } finally {
                setLoading(false);
            }
        },
    });

    const onChangeMethod = (method_id) => {
        const paymentMet = paymentMethods.find(m => { return m.id == method_id })
        setSelectedMethod(paymentMet)

        setChangeMethod(false)
    }

    return (
        <BaseModalAnimated modal={modal} setModal={setModal}>
            <div className="bg-white rounded-2xl w-full mx-auto shadow-xl">

                {/* Header */}
                <div className="mb-2 text-center">
                    <h2 className="text-xl font-bold text-cs-dark">
                        Checkout
                    </h2>
                    <p className="text-sm text-cs-muted">
                        Review your subscription and payment method
                    </p>
                </div>

                {/* Subscription summary */}
                <div className="border rounded-xl p-1 bg-cs-bg mb-2">
                    <div className="flex justify-between items-center mb-3">
                        <div>
                            <h3 className="font-semibold text-cs-dark">
                                {plan.name} Plan
                            </h3>
                            <p className="text-xs text-cs-muted">
                                Monthly billing
                            </p>
                        </div>
                        <span className="text-lg font-bold text-cs-primary">
                            ${price.toFixed(2)}
                        </span>
                    </div>

                    <ul className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm text-cs-dark">
                        <li className="flex items-center gap-1 justify-center">
                            🚚 <b>{plan.trucks}</b> trucks
                        </li>

                        <li className="flex items-center gap-1 justify-center">
                            👥 <b>{plan.users}</b> users
                        </li>

                        {plan.day_trial > 0 && (
                            <li className="flex items-center gap-1 justify-center">
                                ⏱ <b>{plan.day_trial}</b> days free trial
                            </li>
                        )}
                    </ul>
                </div>

                {/* Payment method */}
                <div className="mb-1">
                    <h4 className="text-sm font-semibold text-cs-dark mb-2">
                        Payment method
                    </h4>
                    {addMethod ?
                        <>
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    validation.handleSubmit();
                                    showFormErrors(validation);
                                    return false;
                                }}
                            >
                                <div className="bg-cs-bg rounded-2xl overflow-hidden">
                                    {/* Header contextual */}
                                    <div className="px-5 py-3 border-b border-cs-border bg-cs-surface flex items-center justify-between gap-3">
                                        <div className="flex flex-col gap-0.5">
                                            <p className="text-[10px] uppercase tracking-[0.16em] text-cs-primary-dark">
                                                Add new payment method
                                            </p>
                                            <p className="text-[9px] text-cs-muted">
                                                Your card data is processed securely via Stripe.
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setAddMethod(false)}
                                            className="
                                                px-3 py-1.5 rounded-xl
                                                bg-cs-bg
                                                text-[9px] text-cs-muted
                                                hover:bg-cs-primary-soft hover:text-cs-primary
                                                border border-cs-border
                                                transition-all
                                            "
                                        >
                                            Close
                                        </button>
                                    </div>

                                    {/* Body */}
                                    <div className="p-2 bg-cs-bg-alt grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Card holder */}
                                        <CustomInput
                                            label="Card holder name"
                                            type="text"
                                            name="card_name"
                                            labelClassName="text-[10px] font-medium text-cs-muted mb-1"
                                            error={validation.errors.card_name}
                                            value={validation.values.card_name}
                                            handleChange={validation.handleChange}
                                            placeholder="As shown on the card"
                                        />

                                        {/* Card info (Stripe) */}
                                        <div className="flex flex-col gap-1">
                                            <label className="text-[10px] font-medium text-cs-muted">
                                                Card information
                                            </label>
                                            <div
                                                className="
                                                        w-full py-2 px-2.5 rounded-xl
                                                        border border-cs-border
                                                        bg-cs-bg
                                                        text-[10px]
                                                        focus-within:border-cs-primary
                                                        transition-colors
                                                    "
                                            >
                                                <CardElement
                                                    options={{
                                                        style: {
                                                            base: {
                                                                fontSize: "12px",
                                                                color: "#151515",
                                                                "::placeholder": {
                                                                    color: "#9CA3AF",
                                                                },
                                                            },
                                                            invalid: {
                                                                color: "#EF4444",
                                                            },
                                                        },
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {/* Alias */}
                                        <CustomInput
                                            label="Alias (optional)"
                                            type="text"
                                            name="alias"
                                            labelClassName="text-[10px] font-medium text-cs-muted mb-1"
                                            error={validation.errors.alias}
                                            value={validation.values.alias || ""}
                                            handleChange={validation.handleChange}
                                            
                                            placeholder="e.g. Main card, Operations, Accounting..."
                                        />

                                        {/* Primary radio */}
                                        <div className="flex flex-col gap-1 px-0 md:px-1">
                                            <span className="text-[10px] font-medium text-cs-muted">
                                                Primary payment method
                                            </span>
                                            <div className="flex items-center gap-4 mt-1 text-[9px]">
                                                <label className="flex items-center gap-1.5">
                                                    <input
                                                        type="radio"
                                                        name="principal"
                                                        value="true"
                                                        checked={
                                                            validation.values.principal ===
                                                            true
                                                        }
                                                        onChange={() =>
                                                            validation.setFieldValue(
                                                                "principal",
                                                                true
                                                            )
                                                        }
                                                        className="w-3 h-3"
                                                    />
                                                    <span className="text-cs-dark">
                                                        Yes, set as primary
                                                    </span>
                                                </label>
                                                <label className="flex items-center gap-1.5">
                                                    <input
                                                        type="radio"
                                                        name="principal"
                                                        value="false"
                                                        checked={
                                                            validation.values.principal ===
                                                            false
                                                        }
                                                        onChange={() =>
                                                            validation.setFieldValue(
                                                                "principal",
                                                                false
                                                            )
                                                        }
                                                        className="w-3 h-3"
                                                    />
                                                    <span className="text-cs-muted">
                                                        No, keep as secondary
                                                    </span>
                                                </label>
                                            </div>
                                            {validation.errors.principal && (
                                                <p className="text-cs-danger text-[8px] mt-1">
                                                    {validation.errors.principal}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Footer */}
                                    <div className="px-5 py-4 bg-cs-bg border-t border-cs-border flex justify-end">
                                        <CustomButton
                                            loading={loading}
                                            textLoad="Saving..."
                                            type="success"
                                        >
                                            {validation.values.id
                                                ? "Save changes"
                                                : "Add method"}
                                        </CustomButton>
                                    </div>
                                </div>
                            </form>
                        </>
                        :
                        <>
                            {paymentMethods?.length > 0 ? (
                                <div className="border rounded-xl p-4 bg-cs-bg flex items-center justify-between gap-4">
                                    {/* Left */}
                                    <div className="flex items-center gap-3">
                                        {(changeMethod || !selectedMethod.id) ?
                                            <>
                                                <CustomSelect
                                                    label={'Payment Method'}
                                                    name="selectedMethod_id"
                                                    options={paymentMethods}
                                                    value={selectedMethod}        // Valor controlado por Formik
                                                    handleChange={(e) => onChangeMethod(e.target.value)}
                                                />
                                            </>
                                            :
                                            <>
                                                {/* Brand bubble */}
                                                <div className="
                                                w-10 h-10 rounded-full
                                                bg-cs-primary-soft
                                                text-cs-primary
                                                flex items-center justify-center
                                                text-sm font-bold
                                            "
                                                >
                                                    💳
                                                </div>
                                                {/* Card info */}
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-semibold text-cs-dark">
                                                        {selectedMethod.alias || "Payment card"}
                                                    </span>
                                                    <span className="text-xs text-cs-muted">
                                                        •••• {selectedMethod.card}
                                                    </span>
                                                    <span className="text-[10px] text-cs-muted">
                                                        {selectedMethod.name}
                                                    </span>
                                                </div>
                                            </>
                                        }
                                    </div>


                                    {/* Change */}
                                    <button
                                        type="button"
                                        onClick={() => setChangeMethod(true)}
                                        className="
                                            text-xs text-cs-primary
                                            font-medium
                                            hover:underline
                                            "
                                    >
                                        Change
                                    </button>
                                </div>
                            ) : (
                                <p className="text-sm text-cs-muted mb-2">
                                    No payment methods added yet
                                </p>
                            )}

                            <button
                                type="button"
                                onClick={() => setAddMethod(true)}
                                className="text-sm text-cs-primary font-medium hover:underline"
                            >
                                + Add payment method
                            </button>
                        </>
                    }
                </div>

                {/* Price breakdown */}
                <div className="border-t text-sm mb-1">
                    <div className="flex justify-between ">
                        <span className="text-cs-muted">Subtotal</span>
                        <span>${price.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between ">
                        <span className="text-cs-muted">Taxes ({taxes}%)</span>
                        <span>${taxAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-semibold text-base border-t pt-1">
                        <span>Total</span>
                        <span className="text-cs-primary">
                            ${total.toFixed(2)}
                        </span>
                    </div>
                </div>

                {/* CTA */}
                <CustomButton
                    loading={loading}
                    onClick={save}
                    className="w-full"
                    disabled={!selectedMethod}
                >
                    Confirm & Pay
                </CustomButton>

                <p className="text-[11px] text-cs-muted text-center mt-1">
                    🔒 Secure payment Stripe · Cancel anytime
                </p>
            </div>
        </BaseModalAnimated>
    );
}